import i18next from 'i18next'
import { nil } from 'util/null'
import { parseIntFinite } from 'util/number'

type DateLike = Date | string | number | nil

const MONTH_SHORT_NAMES = {
  en: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'June',
    'July',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
  ],
  ru: [
    'Янв',
    'Февр',
    'Мар',
    'Апр',
    'Мая',
    'Июня',
    'Июля',
    'Авг',
    'Сент',
    'Окт',
    'Нояб',
    'Дек',
  ],
}
const MONTH_NAMES = {
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  ru: [
    'Января',
    'Февраля',
    'Марта',
    'Апреля',
    'Мая',
    'Июня',
    'Июля',
    'Августа',
    'Сентября',
    'Октября',
    'Ноября',
    'Декабря',
  ],
}

export function parseDate(value?: string | number | nil): Date | undefined {
  if (!value) return undefined
  try {
    if (typeof value === 'number') return new Date(value)

    const time = parseIntFinite(value)
    const timestamp = time != null && time.toFixed() === value
    if (timestamp) return new Date(time)

    const date = new Date(value)
    return isNaN(date.getTime()) ? undefined : date
  } catch (error) {
    console.error('parse date failed', value, error)
    return undefined
  }
}

function ru() {
  return !!i18next.resolvedLanguage?.startsWith('ru')
}

function getMonthShortName(month: number): string {
  const names = MONTH_SHORT_NAMES[ru() ? 'ru' : 'en']
  return names[month] || month.toFixed()
}

function getMonthName(month: number): string {
  const names = MONTH_NAMES[ru() ? 'ru' : 'en']
  return names[month] || month.toFixed()
}

export function formatDate(date: Date, format?: string): string
export function formatDate(date: DateLike, format?: string): string | undefined
export function formatDate(date: DateLike, format = 'DD MMMM YYYY'): string | undefined {
  const source = date instanceof Date ? new Date(date.getTime()) : parseDate(date)
  if (!source) return undefined

  source.setMinutes(source.getMinutes() - source.getTimezoneOffset())
  const iso = source.toISOString()
  return format.replace(/(YYYY|MMMM|MMM|MM|DD|HH|mm|ss)/g, pattern => {
    switch (pattern) {
      case 'YYYY':
        return iso.substring(0, 4)
      case 'MMMM':
        return getMonthName(source.getUTCMonth())
      case 'MMM':
        return getMonthShortName(source.getUTCMonth())
      case 'MM':
        return iso.substring(5, 7)
      case 'DD':
        return iso.substring(8, 10)
      case 'HH':
        return iso.substring(11, 13)
      case 'mm':
        return iso.substring(14, 16)
      case 'ss':
        return iso.substring(17, 19)
      default:
        return pattern
    }
  })
}

export function formatDateTime(date: Date): string
export function formatDateTime(date: DateLike): string | undefined
export function formatDateTime(date: DateLike): string | undefined {
  const day = formatDate(date)
  const time = formatTime(date)
  if (day === undefined || time === undefined) return undefined
  return day + ' ' + time
}

export function formatDateTimeShort(date: Date): string
export function formatDateTimeShort(date: DateLike): string | undefined
export function formatDateTimeShort(date: DateLike): string | undefined {
  const pattern = ru() ? 'DD.MM.YYYY HH:mm' : 'MM/DD/YYYY HH:mm'
  return formatDate(date, pattern)
}

export function formatTime(date: Date): string
export function formatTime(date: DateLike): string | undefined
export function formatTime(date: DateLike): string | undefined {
  return formatDate(date, 'HH:mm')
}

export function formatDateSec(date: Date): string
export function formatDateSec(date: DateLike): string | undefined
export function formatDateSec(date: DateLike): string | undefined {
  return formatDate(date, 'YYYY-MM-DD HH:mm:ss')
}
